import { ScheduleExceptionInterface } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Input } from "@swivl-mono/ui";
import { useState } from "react";
import { TbX } from "react-icons/tb";

import { ClosurePeriodFields, ClosureTypeChoice, closureSummary, useClosureDraft } from "./ClosureFields";
import { PageScope } from "./Scope";

interface ExceptionListProps {
  scope: PageScope;
  slug: string;
  /** Overrides already persisted for this scope. */
  exceptions: ScheduleExceptionInterface[];
  /** Added in this edit session, not yet written. */
  staged: Partial<ScheduleExceptionInterface>[];
  /** Saved overrides removed in this session, not yet written. */
  deletedIds: number[];
  /** The parent card is in edit mode — show add / remove affordances. */
  isEditing: boolean;
  onStage: (exception: Partial<ScheduleExceptionInterface>) => void;
  onUnstage: (index: number) => void;
  onDelete: (exceptionId: number) => void;
}

/**
 * One-off date overrides for a schedule, e.g. "Closed for maintenance Aug 5" or
 * "Christmas Eve, 9am–1pm only".
 *
 * Adds and removals are staged rather than written immediately — the card's Save
 * commits them alongside the weekly grid, and Cancel drops them. One Save then
 * means "everything I changed on this card", rather than the grid and the
 * overrides having separate save models.
 */
export function ExceptionList({
  scope,
  slug,
  exceptions,
  staged,
  deletedIds,
  isEditing,
  onStage,
  onUnstage,
  onDelete
}: ExceptionListProps): React.ReactElement | null {
  const [adding, setAdding] = useState(false);
  const [draftDate, setDraftDate] = useState("");
  const [draftReason, setDraftReason] = useState("");
  const closure = useClosureDraft();

  const visible = exceptions.filter((ex) => !deletedIds.includes(ex.id));

  // Nothing to say when there are none and you can't add any.
  if (!isEditing && visible.length === 0) return null;

  const resetDraft = (): void => {
    setDraftDate("");
    setDraftReason("");
    closure.reset();
    setAdding(false);
  };

  const handleAdd = (): void => {
    if (!draftDate || !closure.isValid) return;
    onStage({
      groupId: scope.kind === "group" ? (scope.groupId ?? null) : null,
      locationId: scope.kind === "location" ? (scope.locationId ?? null) : null,
      slug,
      date: draftDate,
      isClosed: closure.type === "closed",
      periods: closure.toPeriods(),
      reason: draftReason.trim() || (closure.type === "closed" ? "Closed" : "Reduced hours")
    });
    resetDraft();
  };

  return (
    <div className="border-t border-grey-200">
      <div className="mx-4 flex items-center justify-between gap-4 py-3">
        <div className="min-w-0">
          <h4 className="body-sm-alt text-grey-900">Date overrides</h4>
          <p className="body-sm mt-0.5 text-grey-600">One-off changes for a specific date, like a holiday closure or a half day.</p>
        </div>
        {isEditing && !adding && (
          <Button variant={ButtonVariant.TEXT} onClick={() => setAdding(true)}>
            Add date override
          </Button>
        )}
      </div>

      {visible.length === 0 && staged.length === 0 ? (
        <p className="body-sm mx-4 pb-3 text-grey-500">None configured.</p>
      ) : (
        <div>
          {visible.map((ex) => (
            <ExceptionRow
              key={ex.id}
              date={ex.date}
              summary={closureSummary(ex.isClosed, ex.periods)}
              reason={ex.reason}
              isEditing={isEditing}
              onRemove={() => onDelete(ex.id)}
            />
          ))}
          {staged.map((ex, idx) => (
            <ExceptionRow
              key={`staged-${idx}`}
              date={ex.date ?? ""}
              summary={closureSummary(ex.isClosed === true, ex.periods)}
              reason={ex.reason}
              isEditing={isEditing}
              isUnsaved
              onRemove={() => onUnstage(idx)}
            />
          ))}
        </div>
      )}

      {adding && (
        <div className="mx-4 my-3 space-y-3 rounded-sm border border-grey-200 p-3">
          <Input
            type="date"
            label="Date"
            required
            value={draftDate}
            onChange={(e) => setDraftDate(e.target.value)}
            containerClassName="max-w-[220px]"
          />
          <Input label="Reason" value={draftReason} onChange={(e) => setDraftReason(e.target.value)} placeholder="e.g. Closed for maintenance" />
          <ClosureTypeChoice name={`exception-${slug}`} label="On this date, the schedule is" closure={closure} />
          <ClosurePeriodFields closure={closure} />
          <div className="flex items-center gap-2">
            <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} onClick={resetDraft}>
              Cancel
            </Button>
            <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} onClick={handleAdd} disabled={!draftDate || !closure.isValid}>
              Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface ExceptionRowProps {
  date: string;
  summary: string;
  reason?: string;
  isEditing: boolean;
  /** Staged in this session — flagged so it doesn't read as already saved. */
  isUnsaved?: boolean;
  onRemove: () => void;
}

function ExceptionRow({ date, summary, reason, isEditing, isUnsaved, onRemove }: ExceptionRowProps): React.ReactElement {
  return (
    <div className="mx-4 grid grid-cols-[200px_1fr_auto] items-center gap-x-4 border-t border-grey-200 py-3">
      <div className="flex min-w-0 items-center gap-2">
        <span className="body-sm-alt text-grey-900">{date}</span>
        {isUnsaved && <span className="body-xs rounded-xs bg-grey-100 px-1 py-0.5 text-grey-700">Unsaved</span>}
      </div>
      <div className="min-w-0">
        <p className="body-sm text-grey-900">{summary}</p>
        {reason && <p className="body-sm mt-0.5 text-grey-600">{reason}</p>}
      </div>
      {isEditing && (
        <button
          type="button"
          onClick={onRemove}
          className="cursor-pointer text-grey-400 hover:text-red-500"
          title="Remove this date override"
          aria-label={`Remove date override for ${date}`}
        >
          <TbX className="size-4" />
        </button>
      )}
    </div>
  );
}
